/**
 * @fileoverview Derives shared conductor progress from the canonical verdict.
 * @module inspection-renderer/shell
 *
 * Responsibilities:
 * - Convert visible form completion summaries and a completion verdict into
 *   the progress percentage, complete gate, and missing-item list.
 *
 * Dependencies:
 * - @firesquirrel/core-domain CompletionVerdict.
 *
 * Architecture:
 * - Pure logic; the verdict remains the sole authority for completion.
 *
 * Notes:
 * - Form summaries are presentation data already derived from the verdict.
 */
import type { CompletionVerdict } from '@firesquirrel/core-domain';
import type { InspectionVisibleFormSummary } from './FormStepTabs';

/**
 * @contract ConductorProgress
 * Purpose: Render-ready inspection completion state.
 * Fields: percent, canComplete, missingItems.
 */
export interface ConductorProgress {
  percent: number;
  canComplete: boolean;
  missingItems: CompletionVerdict['blocking'];
}

/**
 * @reusable deriveConductorProgress
 * Purpose: Derive presentation progress without duplicating completion rules.
 * Inputs: The canonical completion verdict and visible form summaries.
 * Output: Progress percentage, complete gate, and verdict blocking issues.
 * Rules: Completion is permitted only when the verdict permits it.
 */
export function deriveConductorProgress(
  verdict: CompletionVerdict,
  forms: readonly InspectionVisibleFormSummary[],
): ConductorProgress {
  const completedForms = forms.filter((form) => form.complete).length;
  const formProgress = forms.length === 0
    ? 0
    : Math.round((completedForms / forms.length) * 100);

  return {
    percent: forms.length === 0 ? 0 : verdict.canComplete ? 100 : Math.min(formProgress, 99),
    canComplete: forms.length > 0 && verdict.canComplete,
    missingItems: verdict.blocking,
  };
}
